/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { X, Calendar, ChevronDown } from "lucide-react";

export default function UtilitiesFilterDashboard({
    propertyData,
    onFiltersChange,
    initialMonth = new Date().getMonth() + 1,
    initialYear = new Date().getFullYear(),
    initialApartment = "all" }) {
    const [selectedApartment, setSelectedApartment] = useState("");
    const [selectedUtility, setSelectedUtility] = useState("");
    const [selectedPeriod, setSelectedPeriod] = useState("month");
    const [showDateModal, setShowDateModal] = useState(false);
    const [dateRange, setDateRange] = useState({
        start: new Date(),
        end: new Date()
    });


    const [selectedMonth, setSelectedMonth] = useState(initialMonth);
    const [selectedYear, setSelectedYear] = useState(initialYear);



    const months = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
    ];
    const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

    const handlePeriodChange = (e) => {
        const value = e.target.value;
        setSelectedPeriod(value);

        if (value === "custom-range") {
            setShowDateModal(true);
        } else if (value === "month-year") {
            setShowDateModal(true);
        }
    };

    const handleDateRangeChange = (type, value) => {
        setDateRange(prev => ({
            ...prev,
            [type]: new Date(value)
        }));
    };

    const applyDateSelection = () => {
        setShowDateModal(false);
        if (selectedPeriod === "month-year") {
            console.log(`Selected month/year: ${months[selectedMonth]} ${selectedYear}`);
        } else {
            console.log("Date range selected:", dateRange);
        }
    };


    const handleApartmentChange = (e) => {
        const apartmentId = e.target.value;
        setSelectedApartment(apartmentId);
        setSelectedUtility(""); // Reset utility when apartment changes
    };

    const handleUtilityChange = (e) => {
        const utilityId = e.target.value;
        setSelectedUtility(utilityId);
    };

    useEffect(() => {
        if (onFiltersChange) {
            onFiltersChange({
                apartment: selectedApartment,
                month: selectedMonth,
                year: selectedYear,
                period: selectedPeriod
            });
        }
    }, [selectedApartment, selectedMonth, selectedYear, selectedPeriod]);



    // Get selected apartment data
    const selectedApartmentData = propertyData?.find(apt => apt.apartment_id.toString() === selectedApartment);

    return (
        <>
            {/* Apartment Select */}
            <div className="relative flex-1 sm:flex-none sm:w-[220px]">
                <select
                    value={selectedApartment}
                    onChange={handleApartmentChange}
                    className="block shadow-sm px-4 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:ring-blue-500 w-full"
                >
                    <option value="">Select Property</option>
                    {propertyData?.map(apartment => (
                        <option key={apartment.apartment_id} value={apartment.apartment_id}>
                            {apartment.apartment_name}
                        </option>
                    ))}
                </select>
            </div>

            {/* Utility Select - only shown when apartment is selected */}
            {selectedApartment && (
                <div className="relative flex-1 sm:flex-none sm:w-[220px]">
                    <select
                        value={selectedUtility}
                        onChange={handleUtilityChange}
                        className="block shadow-sm px-4 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:ring-blue-500 w-full"
                    >
                        <option value="">Select Utility</option>
                        {selectedApartmentData?.expenses?.length !== 0 ? (
                            selectedApartmentData.expenses.map(expense => (
                                <option key={expense.expense_id} value={expense.expense_id}>
                                    {expense.expense_name}
                                </option>
                            ))
                        ) : (
                            <option value="" disabled>No utilities available</option>
                        )}
                    </select>
                </div>
            )}

            {/* Period Select */}
            <div className="relative flex-1 sm:flex-none sm:w-[180px]">
                <select
                    value={selectedPeriod}
                    onChange={handlePeriodChange}
                    className="block shadow-sm px-4 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:ring-blue-500 w-full"
                >
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="year">This Year</option>
                    <option value="month-year">Specific Month</option>
                    <option value="custom-range">Custom Range</option>
                </select>
            </div>

            {/* Display selected period when custom is chosen */}
            {(selectedPeriod === "custom-range" || selectedPeriod === "month-year") && (
                <button
                    onClick={() => setShowDateModal(true)}
                    className="flex items-center gap-2 hover:bg-gray-50 shadow-sm px-4 py-2 border border-gray-300 rounded-md"
                >
                    <Calendar className="w-4 h-4 text-gray-500" />
                    <span>
                        {selectedPeriod === "month-year"
                            ? `${months[selectedMonth]} ${selectedYear}`
                            : `${dateRange.start.toLocaleDateString()} - ${dateRange.end.toLocaleDateString()}`
                        }
                    </span>
                    <ChevronDown className="w-4 h-4 text-gray-500" />
                </button>
            )}

            {/* Date Selection Modal */}
            {showDateModal && (
                <div className="z-50 fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 p-4">
                    <div className="bg-white shadow-xl rounded-lg w-full max-w-md">
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <h3 className="font-semibold text-gray-900 text-lg">
                                {selectedPeriod === "month-year" ? "Select Month" : "Select Date Range"}
                            </h3>
                            <button
                                onClick={() => setShowDateModal(false)}
                                className="text-gray-400 hover:text-gray-500"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="space-y-6 p-6">
                            {selectedPeriod === "month-year" ? (
                                <div className="gap-4 grid grid-cols-2">
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                                            <Calendar className="w-4 h-4" />
                                            Month
                                        </div>
                                        <select
                                            value={selectedMonth}
                                            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                                            className="block shadow-sm px-4 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:ring-blue-500 w-full"
                                        >
                                            {months.map((month, index) => (
                                                <option key={month} value={index}>{month}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                                            <Calendar className="w-4 h-4" />
                                            Year
                                        </div>
                                        <select
                                            value={selectedYear}
                                            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                                            className="block shadow-sm px-4 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:ring-blue-500 w-full"
                                        >
                                            {years.map(year => (
                                                <option key={year} value={year}>{year}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                                            <Calendar className="w-4 h-4" />
                                            Start Date
                                        </div>
                                        <input
                                            type="date"
                                            value={dateRange.start.toISOString().split('T')[0]}
                                            onChange={(e) => handleDateRangeChange('start', e.target.value)}
                                            className="block shadow-sm px-4 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:ring-blue-500 w-full"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-2 text-gray-500 text-sm">
                                            <Calendar className="w-4 h-4" />
                                            End Date
                                        </div>
                                        <input
                                            type="date"
                                            value={dateRange.end.toISOString().split('T')[0]}
                                            onChange={(e) => handleDateRangeChange('end', e.target.value)}
                                            className="block shadow-sm px-4 py-2 border border-gray-300 focus:border-blue-500 rounded-md focus:ring-blue-500 w-full"
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                        <div className="flex justify-end gap-3 px-6 py-4 border-t">
                            <button
                                onClick={() => setShowDateModal(false)}
                                className="hover:bg-gray-50 px-4 py-2 border border-gray-300 rounded-md text-gray-700 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={applyDateSelection}
                                className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-md text-white transition-colors"
                            >
                                Apply Selection
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}